/**
 * Undo and redo for the builder's draft, as plain data so it is testable
 * without a page: the list and the designer edit the same draft, so one
 * history serves both.
 *
 * Every change is recorded, but changes that come quickly one after another
 * (typing a name, dragging a number) are folded into one step, so Undo takes
 * back a word or a drag rather than a letter. At most `MAX_HISTORY` steps are
 * kept.
 */

export const MAX_HISTORY = 100;
/** Changes closer together than this are one step to undo. */
export const FOLD_MS = 600;

export type History<T> = {
  past: T[];
  present: T;
  future: T[];
  /** When the present was last changed, for folding quick changes together. */
  at: number;
};

export function startHistory<T>(present: T): History<T> {
  return { past: [], present, future: [], at: 0 };
}

/** A new present. Quick changes fold into the step before; any change clears Redo. */
export function record<T>(h: History<T>, next: T, now: number): History<T> {
  if (Object.is(next, h.present)) return h;
  const fold = h.past.length > 0 && now - h.at < FOLD_MS;
  const past = fold ? h.past : [...h.past, h.present].slice(-MAX_HISTORY);
  return { past, present: next, future: [], at: now };
}

/** Start again from this present, with nothing to undo (after a save, say). */
export function reset<T>(present: T): History<T> {
  return startHistory(present);
}

export function undo<T>(h: History<T>): History<T> {
  const prev = h.past[h.past.length - 1];
  if (prev === undefined) return h;
  return { past: h.past.slice(0, -1), present: prev, future: [h.present, ...h.future], at: 0 };
}

export function redo<T>(h: History<T>): History<T> {
  const [next, ...rest] = h.future;
  if (next === undefined) return h;
  return { past: [...h.past, h.present], present: next, future: rest, at: 0 };
}

export const canUndo = (h: History<unknown>) => h.past.length > 0;
export const canRedo = (h: History<unknown>) => h.future.length > 0;
